import React, { JSX, useEffect, useState } from 'react';
import YAML from 'yaml';
import './Footer.css';

interface LinkItem {
  name: string;
  url: string;
}

interface Section {
  title: string;
  links: LinkItem[];
}

interface SocialLink {
  platform: string;
  url: string;
}

interface FooterConfig {
  logo: {
    src: string;
    alt: string;
    tagline: string;
  };
  sections: Section[];
  social: SocialLink[];
  copyright: {
    text: string;
  };
}

const socialIcons: Record<string, JSX.Element> = {
  twitter: (
    <svg
      fill="currentColor"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M23.4 4.8a9.4 9.4 0 0 1-2.7.7 4.7 4.7 0 0 0 2-2.6 9.4 9.4 0 0 1-3 1.2 4.7 4.7 0 0 0-8 4.3 13.4 13.4 0 0 1-9.7-4.9 4.7 4.7 0 0 0 1.4 6.3 4.7 4.7 0 0 1-2.1-.6v.1a4.7 4.7 0 0 0 3.8 4.6 4.7 4.7 0 0 1-2.1.1 4.7 4.7 0 0 0 4.4 3.3 9.5 9.5 0 0 1-5.8 2 13.3 13.3 0 0 0 7.2 2.1c8.7 0 13.4-7.2 13.4-13.4v-.6a9.5 9.5 0 0 0 2.4-2.5z" />
    </svg>
  ),
  linkedin: (
    <svg
      fill="currentColor"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8.98h5v14H0v-14zm7.5 0h4.8v1.9h.1c.7-1.3 2.5-2.6 5.2-2.6 5.6 0 6.6 3.6 6.6 8.3v9.4h-5v-8.3c0-2-.1-4.5-2.8-4.5-2.8 0-3.2 2.2-3.2 4.4v8.4h-5v-16z" />
    </svg>
  ),
  youtube: (
    <svg
      fill="currentColor"
      viewBox="0 0 24 24"
      width="26"
      height="26"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M23.5 6.2a2.9 2.9 0 0 0-2-2C19.6 4 12 4 12 4s-7.6 0-9.5.2a2.9 2.9 0 0 0-2 2A30.5 30.5 0 0 0 0 12a30.5 30.5 0 0 0 .5 5.8 2.9 2.9 0 0 0 2 2c1.9.2 9.5.2 9.5.2s7.6 0 9.5-.2a2.9 2.9 0 0 0 2-2 30.5 30.5 0 0 0 .5-5.8 30.5 30.5 0 0 0-.5-5.8zM9.8 15.6V8.4L16 12l-6.2 3.6z" />
    </svg>
  )
};

const Footer: React.FC = () => {
  const [config, setConfig] = useState<FooterConfig | null>(null);

  useEffect(() => {
    fetch('/config/footer.yaml')
      .then(res => res.text())
      .then(text => setConfig(YAML.parse(text)))
      .catch(console.error);
  }, []);

  if (!config) return null;

  return (
    <footer className="footer">
      <div className="footer-top">
        {/* Logo removed per global enterprise standard */}

        <div className="footer-links">
          {config.sections.map((section, idx) => (
            <div key={idx} className="footer-section">
              <h4>{section.title}</h4>
              <ul>
                {section.links.map((link, i) => (
                  <li key={i}>
                    <a href={link.url}>{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="footer-social">
          {config.social.map((item, i) => (
            <a key={i} href={item.url} target="_blank" rel="noopener noreferrer">
              {socialIcons[item.platform.toLowerCase()]}
            </a>
          ))}
        </div>
      </div>

      <div className="footer-bottom">
        <p>{config?.copyright?.text}</p>
      </div>
    </footer>
  );
};

export default Footer;
